<script>
import axios from "axios";
import Layout from "../../layouts/auth";
import { authMethods } from "@/state/helpers";
import appConfig from "@/app.config";
import { required, email, helpers } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";

export default {
  setup() {
    return { v$: useVuelidate() };
  },
  validations: {
    email: {
      required: helpers.withMessage("Email is required", required),
      email: helpers.withMessage("Please enter valid email", email),
    },
  },
  page: {
    title: "Forgot Password",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },
  components: {
    Layout,
  },
  data() {
    return {
      email: "",
      submitted: false,
      error: null,
      tryingToReset: false,
      isResetError: false,
      isSuccess: false,
      successMsg: "",
    };
  },
  methods: {
    ...authMethods,
    // Go back to wherever the user came from (login or staff login);
    // fall back to the applicant login when opened directly.
    goBack() {
      if (window.history.state && window.history.state.back) {
        this.$router.back();
      } else {
        this.$router.push("/login");
      }
    },
  async tryToReset() {
    this.submitted = true;
      this.v$.$touch();

      if (this.v$.$invalid) {
        return;
      }

      this.tryingToReset = true;
      this.error = null;
      this.isResetError = false;
      this.isSuccess = false;

      try {
        await axios.post("/accounts/forgot_password/", {
          email: this.email,
        });

        this.isSuccess = true;
        this.successMsg =
          "If your email is registered, password reset instructions have been sent.";

      } catch (err) {
        const statusCode = err.response?.status;
        const responseData = err.response?.data;

        if (statusCode === 403) {
          this.error =
            responseData?.msg ||
            "Your account is deactivated. Please contact Administrator.";
        } else if (statusCode === 400) {
          this.error =
            responseData?.error ||
            responseData?.msg ||
            "Please enter a valid email address.";
        } else if (statusCode === 429) {
          this.error =
            responseData?.detail ||
            responseData?.msg ||
            "Too many requests. Please try again later.";
        } else {
          this.error =
            responseData?.msg ||
            responseData?.error ||
            responseData?.detail ||
            "Password reset request failed. Please try again.";
        }

        this.isResetError = true;

      } finally {
        this.tryingToReset = false;
      }
    },
  },
};
</script>

<template>
  <Layout>
    <div class="eauth-shell">
      <div class="eauth-card">
        <img
          class="eauth-logo"
          src="@/assets/images/ECU/ECU-logo.jpg"
          alt="Eastern Credit Union"
        />
        <div class="eauth-accent"></div>

        <!-- Success state -->
        <div v-if="isSuccess" class="eauth-status">
          <div class="eauth-status__icon"><i class="mdi mdi-email-check-outline"></i></div>
          <h1 class="eauth-title">Check Your Email</h1>
          <p class="eauth-sub">{{ successMsg }}</p>
          <router-link to="/staff_login" class="eauth-btn eauth-btn--primary eauth-btn--block">
            <i class="mdi mdi-arrow-left"></i> Back to Login
          </router-link>
        </div>

        <!-- Request form -->
        <form v-else @submit.prevent="tryToReset" novalidate>
          <h1 class="eauth-title">Forgot Your Password?</h1>
          <p class="eauth-sub">
            Enter your registered email and we'll send you instructions to reset
            your password.
          </p>

          <div v-if="isResetError" class="eauth-alert eauth-alert--error" role="alert">
            <i class="mdi mdi-alert-circle-outline"></i>
            <span>{{ error }}</span>
          </div>

          <div class="eauth-group">
            <label class="eauth-label" for="useremail">Email</label>
            <div
              class="eauth-field"
              :class="{ 'is-invalid': submitted && v$.email.$error }"
            >
              <i class="mdi mdi-email-outline eauth-field__icon"></i>
              <input
                type="email"
                v-model.trim="email"
                class="eauth-input"
                id="useremail"
                placeholder="Enter registered email"
                autocomplete="email"
              />
            </div>
            <div
              v-for="(item, index) in v$.email.$errors"
              :key="index"
              class="eauth-error"
            >
              <span v-if="item.$message">{{ item.$message }}</span>
            </div>
          </div>

          <div class="eauth-actions">
            <button class="eauth-btn eauth-btn--ghost" type="button" @click="goBack">
              <i class="mdi mdi-arrow-left"></i> Back
            </button>
            <button
              class="eauth-btn eauth-btn--primary"
              type="submit"
              :disabled="tryingToReset"
            >
              <span v-if="tryingToReset">Sending...</span>
              <template v-else>Reset Password <i class="mdi mdi-arrow-right"></i></template>
            </button>
          </div>
        </form>
      </div>

      <div class="eauth-footer">
        <p>
          © {{ new Date().getFullYear() }} ECU. Crafted with by
          <a target="_blank" rel="noopener" href="https://midriffinfosolution.org/">MISPL</a>
        </p>
      </div>
    </div>
  </Layout>
</template>